import React, { useEffect, useState } from 'react';
import { getSharedBatch } from '../services/api';
import ScoreLegend from './ScoreLegend';
import MatchDetails from './MatchDetails';

function KV({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="kv-row">
      <span className="kv-label">{label}</span>
      <span className="kv-value">{value}</span>
    </div>
  );
}

function fmtS(val) {
  if (val == null) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(n)) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  const display = Number.isInteger(n) ? n.toString() : n.toFixed(2);
  return <span style={{ fontWeight: n > 0 ? 700 : 400, color: n > 0 ? 'var(--green)' : 'var(--text-muted)' }}>{display}</span>;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function getRuleLabel(award) {
  const hasName    = (award.name_match_score    || 0) > 0;
  const hasPurpose = (award.purpose_match_score || 0) > 0;
  const hasProgram = (award.program_match_score || 0) > 0;
  if (hasName && hasPurpose && hasProgram) return 'N+P+Prg';
  if (hasName && (hasPurpose || hasProgram)) return `N+${hasPurpose ? 'P' : 'Prg'}`;
  if (hasName)    return 'Name';
  if (hasPurpose && hasProgram) return 'P+Prg';
  if (hasPurpose) return 'Purpose';
  if (hasProgram) return 'Program';
  return 'Semantic';
}

function Paginator({ total, page, pageSize, onPage, onPageSize }) {
  const totalPages = Math.ceil(total / pageSize) || 1;
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to   = Math.min((page + 1) * pageSize, total);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
      fontSize: 11, color: 'var(--text-muted)',
      borderTop: '1px solid var(--border)', flexWrap: 'wrap',
      background: 'var(--bg-card)',
    }}>
      <span>Rows per page:</span>
      {[10, 25].map(size => (
        <button
          key={size}
          onClick={() => { onPageSize(size); onPage(0); }}
          style={{
            padding: '1px 9px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
            border: '1px solid var(--border)',
            background: pageSize === size ? 'var(--accent)' : 'transparent',
            color: pageSize === size ? '#fff' : 'var(--text-muted)',
            fontWeight: pageSize === size ? 700 : 400,
          }}
        >{size}</button>
      ))}
      <span style={{ marginLeft: 6 }}>{from}–{to} of {total}</span>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
        <button
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}
        >‹ Prev</button>
        <span style={{ fontSize: 11, padding: '0 4px' }}>{page + 1} / {totalPages}</span>
        <button
          disabled={page >= totalPages - 1}
          onClick={() => onPage(page + 1)}
          style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}
        >Next ›</button>
      </div>
    </div>
  );
}

function LineRow({ lineResult, lineNumber, mode }) {
  const [open, setOpen] = useState(false);
  const [expandedAward, setExpandedAward] = useState(null);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [expandedCandidate, setExpandedCandidate] = useState(null);
  const { expenditure: exp, matched_awards: matched = [], unmatched_awards: unmatched = [],
    unrestricted_awards: unrestricted = [],
    expenditure_matched, unmatched_expenditure_reason, ords_notified } = lineResult;

  const topMatch = matched.find(a => a.rank === 1);
  // candidate_count = full pool (stored on result); fallback to matched+unmatched for old records
  const totalCandidates = lineResult.candidate_count ?? (matched.length + unmatched.length);

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      {/* Row header */}
      <div
        className={`batch-exp-row${open ? ' batch-exp-row--open' : ''}`}
        style={{
          background: open ? 'rgba(139,0,0,0.07)' : 'transparent',
          borderLeft: open ? '3px solid rgba(139,0,0,0.35)' : '3px solid transparent',
        }}
        tabIndex={0}
        role="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
      >
        {lineNumber != null && (
          <span style={{ fontSize: 10, color: 'var(--text-muted)', minWidth: 24, textAlign: 'right', flexShrink: 0 }}>
            {lineNumber}
          </span>
        )}
        <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 12 }}>
          {open ? '▼' : '▶'}
        </span>
        <span className="mono" style={{ fontSize: 11, fontWeight: 700, minWidth: 120 }}>
          {exp.exp_id}
        </span>
        {exp.amt != null && (
          <span style={{ fontSize: 11, color: 'var(--accent-hover)', fontWeight: 600, minWidth: 70 }}>
            ${Number(exp.amt).toLocaleString()}
          </span>
        )}
        {/* Category + enriched details */}
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {exp.exp_cat || exp.exp_txn_desc || '—'}
            {exp.exp_type && (
              <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 3, padding: '0 4px' }}>
                {exp.exp_type}
              </span>
            )}
          </span>
          {(exp.task_org_name || exp.proj_name || exp.awd_num) && (
            <span style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {exp.task_org_name && <span>Org: <strong style={{ color: 'var(--text)' }}>{exp.task_org_name}</strong></span>}
              {exp.task_org_name && exp.proj_name && <span style={{ margin: '0 5px' }}>·</span>}
              {exp.proj_name && <span>Proj: <strong style={{ color: 'var(--text)' }}>{exp.proj_name}</strong></span>}
              {exp.awd_num && <span style={{ marginLeft: 5 }}>· Hint: <span className="mono" style={{ color: 'var(--accent-hover)' }}>{exp.awd_num}</span></span>}
            </span>
          )}
        </span>
        {expenditure_matched ? (
          <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {matched.length} match{matched.length !== 1 ? 'es' : ''}
            {topMatch && (
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 5 }}>
                · Top: <span className="mono" style={{ color: 'var(--accent-hover)' }}>{topMatch.awd_num}</span>
                {' '}({topMatch.total_score?.toFixed(1)})
              </span>
            )}
            <span style={{ color: 'var(--text-muted)', marginLeft: 5 }}>· {totalCandidates} candidates</span>
          </span>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>No match</span>
        )}
        {ords_notified != null && (
          <span style={{
            fontSize: 10, borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap',
            color: ords_notified ? 'var(--green)' : 'var(--red)',
            border: `1px solid ${ords_notified ? 'var(--green)' : 'var(--red)'}`,
          }}>
            ORDS {ords_notified ? '✓' : '✗'}
          </span>
        )}
      </div>

      {/* Expanded detail */}
      {open && (
        <div style={{ padding: '8px 16px 12px 32px', background: 'var(--bg-card)' }}>
          {/* Expenditure metadata */}
          {(exp.task_org_name || exp.proj_name || exp.task_name || exp.exp_txn_long_desc) && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
              {exp.task_org_name && <span>Org: <strong style={{ color: 'var(--text)' }}>{exp.task_org_name}</strong></span>}
              {exp.proj_name     && <span>Project: <strong style={{ color: 'var(--text)' }}>{exp.proj_name}</strong></span>}
              {exp.task_name     && <span>Task: <strong style={{ color: 'var(--text)' }}>{exp.task_name}</strong></span>}
              {exp.exp_txn_long_desc && (
                <span style={{ flexBasis: '100%' }}>
                  Desc: <strong style={{ color: 'var(--text)' }}>{exp.exp_txn_long_desc}</strong>
                </span>
              )}
            </div>
          )}

          {expenditure_matched && matched.length > 0 ? (
            <div className="data-table-wrap">
              <table className="data-table compact" style={{ width: '100%', fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>#</th>
                    <th>Award</th>
                    <th>Name</th>
                    <th>Nm</th>
                    <th>Pur</th>
                    <th>Prg</th>
                    <th>Rst</th>
                    <th>Age</th>
                    <th>{mode === 'vector' ? 'Vec' : mode === 'hybrid' ? 'Hyb' : mode === 'vector-llm' ? 'V+L' : 'Sem'}</th>
                    <th>Total</th>
                    <th>Rule</th>
                  </tr>
                </thead>
                <tbody>
                  {matched.map((a, i) => (
                    <React.Fragment key={a.awd_num || i}>
                      <tr
                        className={expandedAward === (a.awd_num || i) ? 'row-expanded' : ''}
                        onClick={e => { e.stopPropagation(); setExpandedAward(expandedAward === (a.awd_num || i) ? null : (a.awd_num || i)); }}
                        style={{ cursor: 'pointer', opacity: i > 0 ? 0.9 : 1 }}
                      >
                        <td>
                          <span className={`rank-dot ${a.rank === 1 ? 'gold' : a.rank === 2 ? 'silver' : a.rank === 3 ? 'bronze' : 'default'}`}>
                            {a.rank ?? i + 1}
                          </span>
                        </td>
                        <td className="mono" style={{ fontWeight: 600 }}>{a.awd_num}</td>
                        <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_name || '—'}</td>
                        <td>{fmtS(a.name_match_score)}</td>
                        <td>{fmtS(a.purpose_match_score)}</td>
                        <td>{fmtS(a.program_match_score)}</td>
                        <td>{fmtS(a.restriction_score)}</td>
                        <td>{fmtS(a.age_score)}</td>
                        <td>{fmtS(a.semantic_score)}</td>
                        <td style={{ fontWeight: 800, color: 'var(--accent-hover)', whiteSpace: 'nowrap' }}>
                          {a.total_score?.toFixed(2) ?? '—'}
                          {a.confidence && (
                            <span className={`confidence-badge ${a.confidence}`} style={{ marginLeft: 4 }}>
                              {a.confidence}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{getRuleLabel(a)}</td>
                      </tr>

                      {expandedAward === (a.awd_num || i) && (
                        <tr className="expanded-row">
                          <td colSpan={11}>
                            <div className="expanded-detail">
                              <div className="kv-grid" style={{ fontSize: 12 }}>
                                <KV label="Organization" value={a.awd_org} />
                                <KV label="Start Date" value={a.awd_start} />
                                <KV label="Purpose" value={a.awd_purpose} />
                                <KV label="Restriction" value={a.awd_restrict} />
                                <KV label="Control" value={a.awd_ctrl} />
                                <KV label="Employee" value={a.awd_role_emp} />
                                <KV label="Person (ORDS)" value={a.awd_person} />
                                <KV label="Purpose (ORDS)" value={a.awd_purpose_desc} />
                                <KV label="Program (ORDS)" value={a.awd_program} />
                              </div>
                              {a.awd_fund_auth && (
                                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                                  <strong>Fund Auth:</strong> {a.awd_fund_auth}
                                </div>
                              )}
                              {a.match_reasons?.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                  <div className="match-evidence-header">
                                    Match Evidence
                                    {a.confidence && (
                                      <span className={`confidence-badge ${a.confidence}`} style={{ marginLeft: 6 }}>
                                        {a.confidence} confidence
                                      </span>
                                    )}
                                  </div>
                                  <ol className="match-reasons-list">
                                    {a.match_reasons.map((reason, ri) => (
                                      <li key={ri} className="match-reason-item">{reason}</li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                              <MatchDetails details={a.match_details} />
                              {a.matched_fields?.length > 0 && (
                                <div className="matched-fields-list" style={{ marginTop: 6 }}>
                                  {a.matched_fields.map((f, fi) => <span key={fi} className="field-tag">{f}</span>)}
                                </div>
                              )}
                              {a.explanation && (
                                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                  <strong style={{ fontStyle: 'normal', color: 'var(--accent-hover)' }}>AI:</strong> {a.explanation}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !expenditure_matched ? (
            <div style={{ fontSize: 11, color: 'var(--red)', padding: '4px 6px' }}>
              No match{unmatched_expenditure_reason ? `: ${unmatched_expenditure_reason}` : ''}
            </div>
          ) : null}

          {(unmatched.length > 0 || unrestricted.length > 0) && (
            <div style={{ marginTop: 8 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', padding: '4px 0' }}
                onClick={e => { e.stopPropagation(); setShowUnmatched(v => !v); }}
              >
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
                  {showUnmatched ? '▼' : '▶'} All Candidates ({matched.length + unmatched.length + unrestricted.length})
                </span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {matched.length} matched · {unmatched.length} not matched{unrestricted.length > 0 ? ` · ${unrestricted.length} unrestricted` : ''}
                </span>
              </div>
              {showUnmatched && (
                <div className="data-table-wrap" style={{ marginTop: 4 }}>
                  <table className="data-table compact" style={{ width: '100%', fontSize: 11 }}>
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Award</th>
                        <th>Name</th>
                        <th>Org</th>
                        <th>Purpose</th>
                        <th>Restriction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matched.map(a => (
                        <React.Fragment key={`m-${a.awd_num}`}>
                          <tr
                            style={{ background: 'rgba(16,185,129,0.05)', cursor: 'pointer' }}
                            onClick={e => { e.stopPropagation(); setExpandedCandidate(expandedCandidate === a.awd_num ? null : a.awd_num); }}
                          >
                            <td><span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 700 }}>#{a.rank} matched {expandedCandidate === a.awd_num ? '▼' : '▶'}</span></td>
                            <td className="mono" style={{ fontWeight: 600 }}>{a.awd_num}</td>
                            <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_name || '—'}</td>
                            <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{a.awd_org}</td>
                            <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.awd_purpose}</td>
                            <td style={{ fontSize: 10, color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_restrict}</td>
                          </tr>
                          {expandedCandidate === a.awd_num && (
                            <tr className="expanded-row">
                              <td colSpan={6}>
                                <div className="expanded-detail">
                                  <div className="kv-grid" style={{ fontSize: 12 }}>
                                    <KV label="Organization" value={a.awd_org} />
                                    <KV label="Start Date" value={a.awd_start} />
                                    <KV label="Purpose" value={a.awd_purpose} />
                                    <KV label="Restriction" value={a.awd_restrict} />
                                    <KV label="Control" value={a.awd_ctrl} />
                                    <KV label="Employee" value={a.awd_role_emp} />
                                    <KV label="Person (ORDS)" value={a.awd_person} />
                                    <KV label="Purpose (ORDS)" value={a.awd_purpose_desc} />
                                    <KV label="Program (ORDS)" value={a.awd_program} />
                                    <KV label="Score" value={a.total_score?.toFixed(2)} />
                                  </div>
                                  {a.awd_fund_auth && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}><strong>Fund Auth:</strong> {a.awd_fund_auth}</div>}
                                  {a.match_reasons?.length > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                      <div className="match-evidence-header">Match Evidence</div>
                                      <ol className="match-reasons-list">
                                        {a.match_reasons.map((r, ri) => <li key={ri} className="match-reason-item">{r}</li>)}
                                      </ol>
                                    </div>
                                  )}
                                  <MatchDetails details={a.match_details} />
                                  {a.explanation && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}><strong style={{ fontStyle: 'normal', color: 'var(--accent-hover)' }}>AI:</strong> {a.explanation}</div>}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {unmatched.map((a, i) => (
                        <React.Fragment key={`u-${a.awd_num || i}`}>
                          <tr
                            style={{ opacity: 0.75, cursor: 'pointer' }}
                            onClick={e => { e.stopPropagation(); setExpandedCandidate(expandedCandidate === `u-${a.awd_num || i}` ? null : `u-${a.awd_num || i}`); }}
                          >
                            <td><span style={{ fontSize: 10, color: 'var(--text-muted)' }}>not matched {expandedCandidate === `u-${a.awd_num || i}` ? '▼' : '▶'}</span></td>
                            <td className="mono">{a.awd_num}</td>
                            <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_name || '—'}</td>
                            <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{a.awd_org}</td>
                            <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.awd_purpose}</td>
                            <td style={{ fontSize: 10, color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_restrict}</td>
                          </tr>
                          {expandedCandidate === `u-${a.awd_num || i}` && (
                            <tr className="expanded-row">
                              <td colSpan={6}>
                                <div className="expanded-detail">
                                  <div className="kv-grid" style={{ fontSize: 12 }}>
                                    <KV label="Organization" value={a.awd_org} />
                                    <KV label="Start Date" value={a.awd_start} />
                                    <KV label="Purpose" value={a.awd_purpose} />
                                    <KV label="Restriction" value={a.awd_restrict} />
                                    <KV label="Control" value={a.awd_ctrl} />
                                    <KV label="Employee" value={a.awd_role_emp} />
                                    <KV label="Person (ORDS)" value={a.awd_person} />
                                    <KV label="Purpose (ORDS)" value={a.awd_purpose_desc} />
                                    <KV label="Program (ORDS)" value={a.awd_program} />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {unrestricted.map((a, i) => (
                        <tr key={`r-${a.awd_num || i}`} style={{ opacity: 0.5 }}>
                          <td><span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>unrestricted</span></td>
                          <td className="mono">{a.awd_num}</td>
                          <td colSpan={4} style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>excluded from scoring (unrestricted award)</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SharedBatch({ batchId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [copied, setCopied]   = useState(false);
  const [expPage, setExpPage]         = useState(0);
  const [expPageSize, setExpPageSize] = useState(10);

  useEffect(() => {
    getSharedBatch(batchId)
      .then(setData)
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [batchId]);

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (loading) {
    return (
      <div className="processing-indicator" style={{ marginTop: 80 }}>
        <div className="spinner" />
        <span>Loading batch results…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div className="error-box" style={{ display: 'inline-block', padding: '16px 24px', fontSize: 14 }}>
          {error === 'Authentication required'
            ? 'This link requires you to be logged in. Please sign in to view.'
            : `Could not load batch: ${error}`}
        </div>
      </div>
    );
  }

  const { batch_name, batch_id, status, matching_mode: mode, results = [],
    lines_total, lines_complete } = data;

  const matchedCount    = results.filter(r => r.expenditure_matched).length;
  const unmatchedCount  = results.filter(r => !r.expenditure_matched).length;
  const totalCandidates = results.reduce((s, r) => s + (r.candidate_count ?? 0), 0);

  const totalLines = results.length;
  const pageStart  = expPage * expPageSize;
  const pagedLines = results.slice(pageStart, pageStart + expPageSize);

  return (
    /* Full-page layout: header pinned, content below scrolls freely */
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <header className="app-header" style={{ flexShrink: 0 }}>
        <div className="app-logo">
          <div className="app-logo-icon">S</div>
          <div>
            <h1>Gift Award Matcher</h1>
            <span>Stanford University &mdash; Shared Batch Results</span>
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <ScoreLegend />
          <button
            className="detail-toggle-btn"
            onClick={handleCopyLink}
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}
          >
            {copied ? '✓ Copied!' : '🔗 Copy Link'}
          </button>
        </div>
      </header>

      {/* Main area fills remaining height, flex column so we can pin paginator */}
      <div style={{
        flex: 1, minHeight: 0,
        display: 'flex', flexDirection: 'column',
        padding: '0 32px 0 32px',
        maxWidth: 1400, width: '100%', margin: '0 auto', boxSizing: 'border-box',
      }}>
        {/* Batch header bar */}
        <div className="detail-bar" style={{ flexShrink: 0 }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {batch_name || `Batch ${batch_id}`}
          </span>
          <span className={`badge ${status === 'COMPLETE' ? 'completed' : status === 'FAILED' ? 'failed' : 'processing'}`}>
            {status}
          </span>
          <span className={`badge ${mode === 'vector' ? 'vector-mode' : mode === 'hybrid' ? 'hybrid-mode' : mode === 'vector-llm' ? 'vector-llm-mode' : 'llm-mode'}`}>
            {(mode || 'llm').toUpperCase()}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {lines_complete} / {lines_total} lines
          </span>
          {matchedCount > 0 && (
            <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>
              {matchedCount} matched
            </span>
          )}
          {unmatchedCount > 0 && (
            <span style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>
              {unmatchedCount} unmatched
            </span>
          )}
          {totalCandidates > 0 && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {totalCandidates.toLocaleString()} candidates
            </span>
          )}
        </div>

        {/* Results card: rows scroll, paginator stays pinned at bottom */}
        {results.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No results available yet.
          </div>
        ) : (
          <div className="card" style={{
            padding: 0,
            flex: 1, minHeight: 0,
            display: 'flex', flexDirection: 'column',
            border: '1px solid var(--border)', borderRadius: 6,
            marginBottom: 16,
          }}>
            {/* Scrollable rows area */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {pagedLines.map((lr, i) => (
                <LineRow
                  key={lr.expenditure?.exp_id ?? i}
                  lineResult={lr}
                  lineNumber={pageStart + i + 1}
                  mode={mode}
                />
              ))}
            </div>
            {/* Paginator always visible at the bottom */}
            <Paginator
              total={totalLines}
              page={expPage}
              pageSize={expPageSize}
              onPage={p => { setExpPage(p); }}
              onPageSize={s => { setExpPageSize(s); setExpPage(0); }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
